import {
  CheckCircle2,
  CircleX,
  Clock3,
  Code2,
  HardDrive,
  Server,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { cn } from "@/shared/lib/cn";
import {
  clearAdminCache,
  fetchAdminCacheStatus,
  type AdminCacheEntry,
  type AdminCacheStatus,
} from "../api/adminOpsApi";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { KpiCard, KpiGrid } from "../components/KpiCard";

export function CacheManagementPage() {
  const { t, locale } = useTranslation();
  const { showToast } = useToast();
  const labels = t.safecheck.system.cache;
  const [status, setStatus] = useState<AdminCacheStatus | null>(null);
  const [entries, setEntries] = useState<AdminCacheEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const response = await fetchAdminCacheStatus();
      setStatus(response);
      setEntries(response.entries ?? []);
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : "Erreur cache.",
        variant: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadStatus();
  }, []);

  const data = status ?? {
    total_entries: 0,
    valid_entries: 0,
    expired_entries: 0,
    total_size_kb: 0,
    max_size_mb: 50,
    usage_percent: 0,
  };

  const usageLabel = labels.stats.usagePercent.replace(
    "{percent}",
    new Intl.NumberFormat(locale === "fr" ? "fr-FR" : "en-US", {
      style: "percent",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(data.usage_percent / 100),
  );

  const spaceBadge = `${data.total_size_kb.toFixed(2)} KB / ${data.max_size_mb} MB`;

  const handleClearAll = async () => {
    setClearing(true);
    try {
      const result = await clearAdminCache();
      showToast({ message: result.message || labels.clearAllSuccess, variant: "success" });
      await loadStatus();
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : labels.clearAllSuccess,
        variant: "danger",
      });
    } finally {
      setClearing(false);
    }
  };

  return (
    <>
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Breadcrumbs homeHref="/dashboard/admin" items={[{ label: t.safecheck.nav.cacheManagement }]} />
        <button
          type="button"
          onClick={handleClearAll}
          disabled={clearing || loading}
          className="inline-flex shrink-0 items-center gap-2 self-start rounded-lg border border-danger px-4 py-2 text-sm font-medium text-danger transition-colors hover:bg-danger-soft disabled:opacity-60 sm:self-auto"
        >
          <Trash2 size={16} />
          {clearing ? "…" : labels.clearAll}
        </button>
      </div>

      <KpiGrid className="grid-cols-1 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard tone="purple" icon={Server} label={labels.stats.totalEntries} value={loading ? "…" : data.total_entries} />
        <KpiCard tone="mint" icon={CheckCircle2} label={labels.stats.validEntries} value={loading ? "…" : data.valid_entries} />
        <KpiCard tone="amber" icon={Clock3} label={labels.stats.expiredEntries} value={loading ? "…" : data.expired_entries} />
        <KpiCard
          tone="sky"
          icon={HardDrive}
          watermark={HardDrive}
          label={labels.stats.totalSize}
          value={loading ? "…" : data.total_size_kb.toFixed(2)}
          unit={loading ? undefined : "KB"}
          hint={usageLabel}
        />
      </KpiGrid>

      <div className="theme-card mt-4 p-5">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-sm font-medium text-muted-foreground">{labels.spaceUsage.title}</h2>
          <span className="rounded-full bg-green/15 px-3 py-1 text-xs font-semibold text-green">
            {spaceBadge}
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-card-muted">
          <div
            className="h-full rounded-full bg-green transition-all"
            style={{ width: `${Math.max(data.usage_percent, 0.5)}%` }}
          />
        </div>
      </div>

      <div className="theme-card mt-4 p-5">
        <h2 className="mb-4 text-sm font-semibold text-foreground">{labels.quickActions.title}</h2>
        <div className="flex flex-col gap-3 lg:flex-row">
          <button
            type="button"
            onClick={handleClearAll}
            disabled={clearing}
            className="inline-flex flex-1 items-center justify-center rounded-lg border border-yellow px-4 py-2.5 text-sm font-medium text-yellow transition-colors hover:bg-yellow/10 disabled:opacity-60"
          >
            {labels.quickActions.cleanExpired}
          </button>
          <button
            type="button"
            onClick={() => showToast({ message: labels.clearOpcacheSuccess, variant: "success" })}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-teal px-4 py-2.5 text-sm font-medium text-teal transition-colors hover:bg-teal/10"
          >
            <Code2 size={16} />
            {labels.quickActions.clearOpcache}
          </button>
          <button
            type="button"
            onClick={() => showToast({ message: labels.disableCacheSuccess, variant: "warning" })}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-danger px-4 py-2.5 text-sm font-medium text-danger transition-colors hover:bg-danger-soft"
          >
            <CircleX size={16} />
            {labels.quickActions.disableCache}
          </button>
        </div>
      </div>

      <div className="theme-card theme-card-static mt-4 overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">{labels.entries.title}</h2>
          <span className="flex h-7 min-w-7 items-center justify-center rounded-md bg-purple-soft px-2 text-xs font-semibold text-purple">
            {entries.length}
          </span>
        </div>

        <div className="max-h-[520px] overflow-auto">
          <table className="safecheck-table w-full min-w-[980px]">
            <thead className="sticky top-0 z-10 bg-card">
              <tr className="border-b border-border">
                {[
                  labels.entries.columns.key,
                  labels.entries.columns.size,
                  labels.entries.columns.created,
                  labels.entries.columns.expires,
                  labels.entries.columns.ttl,
                  labels.entries.columns.status,
                ].map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-muted"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted">
                    …
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted">
                    {labels.entries.empty}
                  </td>
                </tr>
              ) : (
                entries.map((entry, index) => (
                  <tr
                    key={entry.id}
                    className={cn(
                      "border-b border-border text-sm",
                      index % 2 === 0 ? "bg-yellow-soft/25" : "bg-card",
                    )}
                  >
                    <td className="max-w-[240px] truncate px-4 py-3 font-medium text-pink">{entry.key}</td>
                    <td className="px-4 py-3 text-muted-foreground">{entry.size}</td>
                    <td className="px-4 py-3 text-muted-foreground">{entry.created_at}</td>
                    <td className="px-4 py-3 text-muted-foreground">{entry.expires_at}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded bg-danger px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                        {labels.entries.expired}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded bg-yellow px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                        {entry.expired ? labels.entries.expired : labels.entries.columns.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
